import { createContext, useState, useEffect, ReactNode, useRef } from 'react';
import { UIContextType, DOMSize, DOMSizeType } from 'interfaces/UIInterfaces';

const defaultContextValue: UIContextType = {
	windowWidth: 0,
	appWidth: 0,
	isEmbedded: false,
	cmsSlug: null,
	cmsThemeSlug: null,
	cmsThemeVersion: null,
	headerName: null,
	headerType: null,
	headerIsSticky: false,
	mobileHeaderName: null,
	mobileHeaderType: null,
	mobileHeaderIsSticky: false,
	mobileHeaderUntilWidth: 0,
	initialTitle: '',
	isSizeOrLess: () => false
};

const UIContext = createContext<UIContextType>(defaultContextValue);

interface UIProviderProps {
	isEmbedded: boolean;
	cmsSlug: string | null;
	cmsThemeSlug: string | null;
	cmsThemeVersion: string | null;
	headerName: string | null;
	headerType: 'id' | 'class' | null;
	headerIsSticky: boolean;
	mobileHeaderName: string | null;
	mobileHeaderType: 'id' | 'class' | null;
	mobileHeaderIsSticky: boolean;
	mobileHeaderUntilWidth: number;
	children: ReactNode;
};

export const UIProvider: React.FC<UIProviderProps> = ({
	isEmbedded,
	cmsSlug,
	cmsThemeSlug,
	cmsThemeVersion,
	headerName,
	headerType,
	headerIsSticky,
	mobileHeaderName,
	mobileHeaderType,
	mobileHeaderIsSticky,
	mobileHeaderUntilWidth,
	children
}) => {
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [appWidth, setAppWidth] = useState(0);
	const appWrapRef = useRef<HTMLDivElement>(null);

	const initialTitle = document.title;

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);

		window.addEventListener('resize', handleResize);

		// Clean up the event listener on component unmount
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const handleResize = (entries: ResizeObserverEntry[]) => {
			if (entries[0]) {
				setAppWidth(entries[0].contentRect.width);
			}
		};

		const resizeObserver = new ResizeObserver(handleResize);

		if (appWrapRef.current) {
			resizeObserver.observe(appWrapRef.current);
		}

		// Clean up the resize observer on component unmount
		return () => {
			if (appWrapRef.current) {
				resizeObserver.unobserve(appWrapRef.current);
			}
		};
	}, [appWrapRef]);

	const isSizeOrLess = (size: DOMSize, type: DOMSizeType = 'app'): boolean => {
		const currentWidth = type === 'app' ? appWidth : windowWidth;
	
		switch (size) {
			case 'xs':
				return currentWidth < 600;
			case 'sm':
				return currentWidth < 900;
			case 'md':
				return currentWidth < 1200;
			case 'lg':
				return currentWidth < 1536;
			case 'xl':
				return currentWidth > 1900;
			default:
				throw new Error('Invalid size');
		}
	};
	
	//Add width and max-width 100% to div, since some WP themes try to force the website width
	
	return (
		<div id="tt-app-wrap" ref={appWrapRef} style={{ width: '100%', maxWidth: '100%' }}>
			<UIContext.Provider value={{
				windowWidth,
				appWidth,
				isEmbedded,
				cmsSlug,
				cmsThemeSlug,
				cmsThemeVersion,
				headerName,
				headerType,
				headerIsSticky,
				mobileHeaderName,
				mobileHeaderType,
				mobileHeaderIsSticky,
				mobileHeaderUntilWidth,
				isSizeOrLess,
				initialTitle
			}}>
				{children}
			</UIContext.Provider>
		</div>
	);
};

export default UIContext;